import React, { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
// react admin
import {
  Pagination,
  Datagrid,
  DateField,
  List,
  NumberField,
  TextField,
  useListContext,
  useListController,
  FunctionField,
  ReferenceField,
  useGetIdentity,
} from "react-admin";
// mui
import {
  Card,
  Button,
  Box,
  TextField as MuiTextField,
  useTheme,
  useMediaQuery,
  Chip,
  Switch,
  FormControlLabel,
} from "@mui/material";
// components
import { GameDetails } from "../Summary/GameDetails";
import CustomPagination from "../../../Components/CustomPagination";
import Parse from "parse";

const CustomFilter = () => {
  const { setFilters } = useListContext();
  const { data: userIdentity } = useGetIdentity();

  const [filterValue, setFilterValue] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [includeTestingUsers, setIncludeTestingUsers] = useState(false);
  const [canMarkTestUsers, setCanMarkTestUsers] = useState(false);

  const today = new Date().toISOString().split("T")[0];

  // Check test user permission
  React.useEffect(() => {
    const checkPermission = async () => {
      try {
        const result = await Parse.Cloud.run("getTestUserPermission");
        setCanMarkTestUsers(result.canMarkTestUsers);
      } catch (error) {
        console.error("Failed to check test user permission:", error);
      }
    };

    if (userIdentity) {
      checkPermission();
    }
  }, [userIdentity]);

  const handleApply = () => {
    const filters = {};
    if (filterValue.trim()) filters.username = filterValue;
    if (startDate) filters.startDate = startDate;
    if (endDate) filters.endDate = endDate;
    filters.includeTestingUsers = includeTestingUsers;

    setFilters(filters, {});
  };

  const handleClear = () => {
    setFilterValue("");
    setStartDate("");
    setEndDate("");
    setIncludeTestingUsers(false);
    setFilters({}, {});
  };

  return (
    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
      <MuiTextField
        size="small"
        variant="outlined"
        value={filterValue}
        onChange={(e) => setFilterValue(e.target.value)}
        placeholder="Enter Username"
      />

      <MuiTextField
        size="small"
        type="date"
        label="Start Date"
        InputLabelProps={{ shrink: true }}
        value={startDate}
        onChange={(e) => {
          const newStart = e.target.value;
          setStartDate(newStart);
          // Clear end date if it's before new start date
          if (endDate && newStart > endDate) {
            setEndDate("");
          }
        }}
        inputProps={{ max: today }}
      />
      <MuiTextField
        size="small"
        type="date"
        label="End Date"
        InputLabelProps={{ shrink: true }}
        value={endDate}
        onChange={(e) => setEndDate(e.target.value)}
        // disabled={!startDate}
        inputProps={{
          min: startDate || undefined,
          max: today,
        }}
      />

      {(userIdentity?.userRoleName === "Super-User" || canMarkTestUsers) && (
        <FormControlLabel
          control={
            <Switch
              checked={includeTestingUsers}
              onChange={(e) => setIncludeTestingUsers(e.target.checked)}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#ffca28",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#ffca28",
                },
                "& .MuiSwitch-track": {
                  backgroundColor: "#555",
                },
              }}
            />
          }
          label="Test Users"
          sx={{
            color: "white",
            fontSize: "14px",
            ml: 1,
            ".MuiFormControlLabel-label": {
              fontSize: "14px",
              color: "white",
            },
          }}
        />
      )}

      <Button
        variant="contained"
        sx={{
          background: "#FFC107",
          color: "#0A0A0A",
          textTransform: "none",
          "&:hover": {
            backgroundColor: "#FFA000",
          },
        }}
        onClick={handleApply}
      >
        Apply
      </Button>
      <Button
        variant="outlined"
        sx={{
          outline: "1px solid #FFC107",
          color: "white",
          textTransform: "none",
        }}
        onClick={handleClear}
      >
        Clear
      </Button>
    </Box>
  );
};

// Custom Pagination Component
const PostPagination = () => {
  const { total, page, perPage, setPage, setPerPage } = useListContext();

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        width: "100% !important",
        margin: "16px 0px",
      }}
    >
       <CustomPagination
                  page={page}
                  perPage={perPage}
                  total={total}
                  setPage={setPage}
                  setPerPage={setPerPage}
                  componentPage="gamecoins"
        />
    </Box>
  );
};

export const GameWiseUserData = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const params = useParams();
  const [searchParams] = useSearchParams();

  const id = params.id;
  const gameType = searchParams.get("gameType");
  const gameName = searchParams.get("gameName");
  const manager = searchParams.get("manager");
  return (
    <React.Fragment>
       <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 80px)'}}>
            <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 2,
      }}>
      <GameDetails summaryData={{ gameType, gameName }} />
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pt: 2 }}>
      <Box
        sx={{
          mt: 2,
          backgroundColor: "#242424",
          borderRadius: 2,
          boxShadow: "none !important",
          padding: isSmallScreen ? { xs: 0, sm: 0 } : { xs: 1.5, sm: 2 },
        }}
      >
        <List
          title="Recharge Records"
          resource="userCoin"
          filters={<CustomFilter />}
          filter={{ gameType: gameType, gameId: id, manager:manager }}
          sort={{ field: "createdAt", order: "DESC" }}
          actions={null}
          perPage={100}
          pagination={<PostPagination />}
          sx={{
            "& .RaList-actions": {
              flexWrap: "nowrap",
            },
            "& .RaFilterFormInput-spacer": { display: "none" },
          }}
        >
          <Box
            style={{
              width: "100%",
              overflowX: "auto",
            }}
          >
            <Box
              style={{
                width: "100%",
                position: isSmallScreen ? "absolute" : "relative",
              }}
            >
              <Datagrid
                bulkActionButtons={false}
                sx={{
                  overflowX: "auto",
                  overflowY: "hidden",
                  width: "100%",
                  maxHeight: "100%",
                  "& .RaDatagrid-table": {
                    width: "100%",
                  },
                }}
              >
               
                <TextField source="username" label="Username" />
                {gameType === "VE" && <TextField source="spinID" label="Spin ID" />}
                <NumberField
                  source="beforeBalance"
                  label="Before Balance"
                  textAlign="left"
                  sortable={false}
                  options={{
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }}
                />
                <NumberField
                  source="betAmount"
                  label="Bet Amount"
                  textAlign="left"
                  options={{
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }}
                />
                <NumberField
                  source="winloseAmount"
                  label="Win Amount"
                  textAlign="left"
                  options={{
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }}
                />
                <NumberField
                  source="afterBalance"
                  label="After Balance"
                  textAlign="left"
                  sortable={false}
                  options={{
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }}
                />
                {gameType === "VE" && (
                  <FunctionField
                    label="Validation"
                    render={(record) => {
                      const validation = record.validation;
                      let color = "default";
                      if (validation === "Both") color = "success";
                      else if (validation === "Bet") color = "warning";
                      else if (validation === "Free Spin") color = "info";
                      else if (validation === "Error") color = "error";
                      
                      return (
                        <Chip
                          label={validation || "-"}
                          color={color}
                          size="small"
                          variant="outlined"
                        />
                      );
                    }}
                  />
                )}
                <DateField source="createdAt" label="Date" showTime />
              </Datagrid>
            </Box>
          </Box>
        </List>
      </Box></Box></Box>
    </React.Fragment>
  );
};
